import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as teamsApi from '../../api/teams'
import * as trainersApi from '../../api/trainers'
import * as linksApi from '../../api/links'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Spinner from '../../components/common/Spinner'
import {
  UserGroupIcon,
  UsersIcon,
  TrashIcon,
  PencilIcon,
  UserPlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

/**
 * Team Detail Page
 * View and manage team information with tabs
 */
export default function TeamDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [activeTab, setActiveTab] = useState('overview')

  // Fetch full team details
  const { data: team, isLoading } = useQuery({
    queryKey: ['team', id, 'full'],
    queryFn: () => teamsApi.getTeamFull(id),
  })

  // Fetch team's trainers
  const { data: trainers } = useQuery({
    queryKey: ['team', id, 'trainers'],
    queryFn: () => teamsApi.getTeamTrainers(id),
    enabled: activeTab === 'trainers',
  })

  // Delete team mutation
  const deleteMutation = useMutation({
    mutationFn: () => teamsApi.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['teams'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Team deleted successfully', 'success')
      navigate('/teams')
    },
    onError: (error) => {
      showToast(error.message || 'Failed to delete team', 'error')
    },
  })

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!team) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Team not found</p>
        <Button 
          variant="primary" 
          onClick={() => navigate('/teams')}
          className="mt-4"
        >
          Back to Teams
        </Button>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: UserGroupIcon },
    { id: 'trainers', label: 'Trainers', icon: UsersIcon },
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <UserGroupIcon className="h-10 w-10 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {team.name}
            </h1>
            <p className="text-gray-600">{team.uid}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/teams/${id}/edit`)}
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
          
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'overview' && <OverviewTab team={team} />}
        {activeTab === 'trainers' && <TrainersTab trainers={trainers} teamId={id} />}
      </div>

      {/* Back Button */}
      <div className="flex justify-start">
        <Button variant="secondary" onClick={() => navigate('/teams')}>
          Back to Teams
        </Button>
      </div>
    </div>
  )
}

// Overview Tab Component
function OverviewTab({ team }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Team Information */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
        </Card.Header>
        <Card.Body>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Team Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{team.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Team ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{team.uid}</dd>
            </div>
            {team.age_group && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Age Group</dt>
                <dd className="mt-1 text-sm text-gray-900">{team.age_group}</dd>
              </div>
            )}
            {team.level && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Level</dt>
                <dd className="mt-1 text-sm text-gray-900">{team.level}</dd>
              </div>
            )}
            {team.season && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Season</dt>
                <dd className="mt-1 text-sm text-gray-900">{team.season}</dd>
              </div>
            )}
          </dl>
        </Card.Body>
      </Card>

      {/* Statistics */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Statistics</h2>
        </Card.Header>
        <Card.Body>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Trainers</dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">
                {team.trainers?.length || 0}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(team.created_at).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(team.updated_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </Card.Body>
      </Card>
    </div>
  )
}

// Trainers Tab Component
function TrainersTab({ trainers, teamId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTrainerId, setSelectedTrainerId] = useState(null)

  // Fetch available trainers
  const { data: trainersData, isLoading: isLoadingTrainers } = useQuery({
    queryKey: ['trainers', { search: searchQuery }],
    queryFn: () => trainersApi.getTrainers({ search: searchQuery, limit: 50 }),
    enabled: showLinkModal,
  })

  // Link trainer mutation
  const linkTrainerMutation = useMutation({
    mutationFn: (trainerId) => linksApi.linkTrainerTeam(trainerId, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries(['team', teamId])
      queryClient.invalidateQueries(['trainers'])
      showToast('Trainer linked successfully', 'success')
      setShowLinkModal(false)
      setSelectedTrainerId(null)
      setSearchQuery('')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to link trainer', 'error')
    },
  })

  // Unlink trainer mutation
  const unlinkTrainerMutation = useMutation({
    mutationFn: (trainerId) => linksApi.unlinkTrainerTeam(trainerId, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries(['team', teamId])
      queryClient.invalidateQueries(['trainers'])
      showToast('Trainer unlinked successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to unlink trainer', 'error')
    },
  })

  const handleUnlinkTrainer = (trainerId, trainerName) => {
    if (window.confirm(`Are you sure you want to unlink ${trainerName}?`)) {
      unlinkTrainerMutation.mutate(trainerId)
    }
  }

  const handleLinkTrainer = () => {
    if (!selectedTrainerId) {
      showToast('Please select a trainer', 'error')
      return
    }
    linkTrainerMutation.mutate(selectedTrainerId)
  }

  const allTrainers = trainersData?.data || []
  const linkedTrainerIds = trainers?.map(t => t.id) || []
  const availableTrainers = allTrainers.filter(t => !linkedTrainerIds.includes(t.id))

  return (
    <>
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Trainers ({trainers?.length || 0})
            </h2>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowLinkModal(true)}
            >
              <UserPlusIcon className="h-4 w-4 mr-1" />
              Link Trainer
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {trainers && trainers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trainers.map((trainer) => (
                <div
                  key={trainer.id}
                  className="flex items-start gap-3 p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors group"
                >
                  <UsersIcon className="h-6 w-6 text-primary-600 mt-0.5" />
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => navigate(`/trainers/${trainer.id}`)}
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {trainer.first_name} {trainer.last_name}
                    </p>
                    <p className="text-xs text-gray-500">{trainer.email}</p>
                    {trainer.uid && (
                      <p className="text-xs text-gray-400">{trainer.uid}</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUnlinkTrainer(trainer.id, `${trainer.first_name} ${trainer.last_name}`)
                    }}
                    className="text-red-600 hover:text-red-700 transition-colors opacity-0 group-hover:opacity-100"
                    title="Unlink trainer"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 mb-3">
                No trainers assigned to this team
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowLinkModal(true)}
              >
                <UserPlusIcon className="h-4 w-4 mr-1" />
                Link Trainer
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Link Trainer Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Link Trainer to Team</h2>
              <button
                onClick={() => {
                  setShowLinkModal(false)
                  setSelectedTrainerId(null)
                  setSearchQuery('')
                }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Trainers
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, email..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Trainers
                </label>
                {isLoadingTrainers ? (
                  <div className="text-center py-8 text-gray-500">Loading trainers...</div>
                ) : availableTrainers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery ? 'No trainers found matching your search' : 'All trainers are already linked'}
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-80 overflow-y-auto">
                    {availableTrainers.map((trainer) => (
                      <label
                        key={trainer.id}
                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                          selectedTrainerId === trainer.id
                            ? 'bg-primary-50 hover:bg-primary-100'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="trainer"
                          value={trainer.id}
                          checked={selectedTrainerId === trainer.id}
                          onChange={() => setSelectedTrainerId(trainer.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {trainer.first_name} {trainer.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{trainer.email}</p>
                          {trainer.uid && (
                            <p className="text-xs text-gray-400">{trainer.uid}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLinkModal(false)
                  setSelectedTrainerId(null)
                  setSearchQuery('')
                }}
                disabled={linkTrainerMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleLinkTrainer}
                loading={linkTrainerMutation.isPending}
                disabled={!selectedTrainerId}
              >
                <UserPlusIcon className="h-4 w-4 mr-1" />
                Link Trainer
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}